import { EventDispatcher, MathUtils, MOUSE, Quaternion, Vector2, Vector3 } from "three";
import { Globals } from "@ortho-next/three-base";
import { ViewType } from "../Models/AppModel.js";
import { bindedModelBase } from "../Models/BindedModel.js";
var State;
(function (State) {
    State[State["none"] = 0] = "none";
    State[State["zoom"] = 1] = "zoom";
    State[State["touchZoom"] = 2] = "touchZoom";
    State[State["pan"] = 3] = "pan";
    State[State["touchPan"] = 4] = "touchPan";
})(State || (State = {}));
export var RotateCameraType;
(function (RotateCameraType) {
    RotateCameraType[RotateCameraType["none"] = 0] = "none";
    RotateCameraType[RotateCameraType["templating"] = 1] = "templating";
})(RotateCameraType || (RotateCameraType = {}));
/**
 * Camera controls created converting trackballcontrols.
 * It works only with orthographic camera and it can only zoom and pan and it supports custom viewport size.
 */
export class CustomCameraControls extends EventDispatcher {
    constructor(camera, viewType, viewportWidth = 1, viewportHeight = 1) {
        super();
        /**
         * Enabled state used to block camera controls if the view is not active in multiple views.
         */
        this.enabled = true;
        /**
         * Indicates if the cursor is on canvas.
         */
        this.cursorOnCanvas = true;
        this._isDragging = false;
        this._screen = { left: 0, top: 0, width: 0, height: 0 };
        this._zoomSpeed = 1.5;
        this._eye = new Vector3();
        this._target = new Vector3();
        this._state = State.none;
        this._touchZoomDistanceStart = 0;
        this._touchZoomDistanceEnd = 0;
        this._zoomStart = new Vector2();
        this._zoomEnd = new Vector2();
        this._panStart = new Vector2();
        this._panEnd = new Vector2();
        this._mouseChange = new Vector2();
        this._objectUp = new Vector3();
        this._pan = new Vector3();
        this._mouseVector = new Vector2();
        this._angle = 0;
        this._isFirstTouch = true;
        this.mousedown = (event) => {
            if (!this.isPossibleToUpdate)
                return;
            if (this._state === State.none) {
                switch (event.button) {
                    case MOUSE.LEFT:
                        this._state = State.pan;
                        break;
                    case MOUSE.MIDDLE:
                        this._state = State.zoom;
                        break;
                }
            }
            if (this._state === State.zoom) {
                this._zoomStart.copy(this.getMouseOnScreen(event.pageX, event.pageY));
                this._zoomEnd.copy(this._zoomStart);
            }
            else if (this._state === State.pan) {
                this._panStart.copy(this.getMouseOnScreen(event.pageX, event.pageY));
                this._panEnd.copy(this._panStart);
            }
            document.addEventListener('mousemove', this.mousemove, false);
            document.addEventListener('mouseup', this.mouseup, false);
        };
        this.mousemove = (event) => {
            if (!this.isPossibleToUpdate)
                return;
            if (this._state === State.zoom) {
                this._zoomEnd.copy(this.getMouseOnScreen(event.pageX, event.pageY));
            }
            else if (this._state === State.pan) {
                this._panEnd.copy(this.getMouseOnScreen(event.pageX, event.pageY));
            }
        };
        this.mouseup = () => {
            this._state = State.none;
            document.removeEventListener('mousemove', this.mousemove);
            document.removeEventListener('mouseup', this.mouseup);
        };
        this.mousewheel = (event) => {
            if (!this.isPossibleToUpdate)
                return;
            switch (event.deltaMode) {
                case 2:
                    return this._zoomStart.y -= event.deltaY * 0.025; // Zoom in pages
                case 1:
                    return this._zoomStart.y -= event.deltaY * 0.01; // Zoom in lines
                default:
                    return this._zoomStart.y -= event.deltaY * 0.00025; // undefined, 0, assume pixels
            }
        };
        this.touchstart = (event) => {
            if (!this.isPossibleToUpdate)
                return;
            switch (event.touches.length) {
                case 1:
                    this._state = State.touchPan;
                    this._panStart.copy(this.getMouseOnScreen(event.touches[0].pageX, event.touches[0].pageY));
                    this._panEnd.copy(this._panStart);
                    break;
                default: // 2 or more {}
                    this._state = State.touchZoom;
                    const dx = event.touches[0].pageX - event.touches[1].pageX;
                    const dy = event.touches[0].pageY - event.touches[1].pageY;
                    this._touchZoomDistanceEnd = this._touchZoomDistanceStart = Math.sqrt(dx ** 2 + dy ** 2);
                    break;
            }
            if (this._isFirstTouch) {
                document.addEventListener('touchend', this.touchend, false);
                document.addEventListener('touchmove', this.touchmove, false);
                this._isFirstTouch = false;
            }
        };
        this.touchmove = (event) => {
            if (!this.isPossibleToUpdate)
                return;
            switch (event.touches.length) {
                case 1:
                    return this._panEnd.copy(this.getMouseOnScreen(event.touches[0].pageX, event.touches[0].pageY));
                default: // 2 or more
                    const dx = event.touches[0].pageX - event.touches[1].pageX;
                    const dy = event.touches[0].pageY - event.touches[1].pageY;
                    this._touchZoomDistanceEnd = Math.sqrt(dx ** 2 + dy ** 2);
            }
            return undefined;
        };
        this.touchend = (event) => {
            switch (event.touches.length) {
                case 0:
                    document.removeEventListener('touchend', this.touchend);
                    document.removeEventListener('touchmove', this.touchmove);
                    this._isFirstTouch = true;
                    return this._state = State.none;
                case 1:
                    this._state = State.touchPan;
                    this._panStart.copy(this.getMouseOnScreen(event.touches[0].pageX, event.touches[0].pageY));
                    return this._panEnd.copy(this._panStart);
            }
            return undefined;
        };
        this.camera = camera;
        this._bindedModelKey = viewType == ViewType.AP ? "scaleHitboxAP" : "scaleHitboxLT";
        this.domElement = Globals.canvasContainer;
        this.viewportWidth = viewportWidth;
        this.viewportHeight = viewportHeight;
        this.handleResize();
        this.bindEvents();
        this.update();
        this.enabled = false;
    }
    /**
     * Set the plan with the image of the x-ray. Must be called during application startup.
     */
    set tools(obj) {
        this._tools = obj;
        this.getUpdatedScaleFactor();
    }
    /**
     * Indicates if user is dragging a 3d object.
     */
    get isDragging() {
        return this._isDragging;
    }
    set isDragging(value) {
        this._isDragging = value;
        if (this._state === State.pan) {
            this.mouseup();
        }
        else {
            this._state = State.none;
        }
    }
    get isPossibleToUpdate() {
        return this.enabled && this.cursorOnCanvas && !this._isDragging;
    }
    bindEvents() {
        this.domElement.addEventListener('mousedown', this.mousedown, false);
        this.domElement.addEventListener('wheel', this.mousewheel, false);
        this.domElement.addEventListener('touchstart', this.touchstart, false);
    }
    /**
     * Recalculates camera aspect ratio. It must be called after window resize.
     */
    handleResize() {
        const box = this.domElement.getBoundingClientRect();
        const d = this.domElement.ownerDocument.documentElement;
        this._screen.left = box.left + window.pageXOffset - d.clientLeft;
        this._screen.top = box.top + window.pageYOffset - d.clientTop;
        this._screen.width = box.width * this.viewportWidth;
        this._screen.height = box.height * this.viewportHeight;
        this.updatePanSpeed();
    }
    updatePanSpeed() {
        this._panSpeed = 1 / (this.camera.position.z - this._target.z) * Globals.canvasContainer.clientWidth;
    }
    update() {
        if (!this.isPossibleToUpdate)
            return;
        this._eye.subVectors(this.camera.position, this._target);
        this.zoomCamera();
        this.panCamera();
        this.camera.position.addVectors(this._target, this._eye);
        this.camera.lookAt(this._target);
    }
    /**
     * Set zoom considering min and max interval.
     */
    zoomCamera() {
        if (this._state === State.touchZoom) {
            const factor = this._touchZoomDistanceStart / this._touchZoomDistanceEnd;
            this._touchZoomDistanceStart = this._touchZoomDistanceEnd;
            this.setZoom(this.camera.zoom / factor, factor);
        }
        else {
            const factor = 1.0 + (this._zoomEnd.y - this._zoomStart.y) * this._zoomSpeed;
            if (factor !== 1.0 && factor > 0.0) {
                this.setZoom(this.camera.zoom / factor, factor);
            }
            this._zoomStart.copy(this._zoomEnd);
        }
    }
    panCamera() {
        this._mouseChange.copy(this._panEnd).sub(this._panStart);
        if (this._mouseChange.lengthSq()) {
            const scaleX = (this.camera.right - this.camera.left) / this.camera.zoom / this.domElement.clientWidth;
            const scaleY = (this.camera.top - this.camera.bottom) / this.camera.zoom / this.domElement.clientWidth;
            this._mouseChange.x *= scaleX;
            this._mouseChange.y *= scaleY;
            this._mouseChange.multiplyScalar(this._eye.length() * this._panSpeed);
            this._pan.copy(this._eye).cross(this.camera.up).setLength(this._mouseChange.x);
            this._pan.add(this._objectUp.copy(this.camera.up).setLength(this._mouseChange.y));
            this.camera.position.add(this._pan);
            this._target.add(this._pan);
            this._panStart.copy(this._panEnd);
            this.dispatchEvent({ type: "pan", args: this._pan });
        }
    }
    getMouseOnScreen(pageX, pageY) {
        this._mouseVector.set((pageX - this._screen.left) / this._screen.width, (pageY - this._screen.top) / this._screen.height);
        return this._mouseVector;
    }
    /**
     * Rotates the camera along the z-axis.
     */
    rotateCamera(angle) {
        const quaternion = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), angle - this._angle);
        this._eye.applyQuaternion(quaternion);
        this.camera.up.applyQuaternion(quaternion);
        this._angle = angle;
        this._eye.subVectors(this.camera.position, this._target);
        this.camera.position.addVectors(this._target, this._eye);
        this.camera.lookAt(this._target);
    }
    setZoom(value, factor = 0) {
        if (value !== this.camera.zoom) {
            this.camera.setZoom(MathUtils.clamp(value, this._minZoom, this._maxZoom));
            bindedModelBase[this._bindedModelKey] = value * this.viewportWidth;
            factor && this.dispatchEvent({ type: "zoom", args: factor });
        }
    }
    getUpdatedScaleFactor() {
        const { A, B, C, D } = this._tools.plane.getVertices(0);
        A.applyEuler(this.camera.rotation);
        B.applyEuler(this.camera.rotation);
        C.applyEuler(this.camera.rotation);
        D.applyEuler(this.camera.rotation);
        const width = Math.max(A.x, B.x, C.x, D.x) - Math.min(A.x, B.x, C.x, D.x);
        const height = Math.max(A.y, B.y, C.y, D.y) - Math.min(A.y, B.y, C.y, D.y);
        const defaultZoom = Math.min(this.camera.height / height, this.camera.width / width);
        this._minZoom = defaultZoom / 10;
        this._maxZoom = defaultZoom * 10;
        return { defaultZoom };
    }
    /**
     * Set real scale on print report. Works for a3 for now.
     */
    setRealScalePrintA3(A3PixelWidth, A3PixelHeight) {
        const A3InchDiagonal = 20.254;
        const A3PixelDiagonal = Math.sqrt(A3PixelWidth ** 2 + A3PixelHeight ** 2);
        const ppi = A3PixelDiagonal / A3InchDiagonal;
        const inchToMM = 25.4;
        const ppmm = ppi / inchToMM;
        this.setZoom(ppmm);
    }
    /**
     * Sets the camera zoom until the plane is completely visible.
     */
    fitToPlane() {
        if (this._tools) {
            const { defaultZoom, box } = this.getUpdatedScaleFactor();
            this.applyFit(defaultZoom, box);
        }
    }
    /**
     * Sets the camera zoom until the circonference that has the plane inside is completely visible.
     */
    fitToPlaneRotation() {
        if (this._tools) {
            const { A, B, C, D } = this._tools.plane.getVertices(0);
            const diagonal = Math.max(Math.hypot(A.x - C.x, A.y - C.y), Math.hypot(B.x - D.x, B.y - D.y));
            const radius = diagonal / 2;
            const diameter = radius * 2;
            const defaultZoom = Math.min(this.camera.height / diameter, this.camera.width / diameter);
            this._minZoom = defaultZoom / 10;
            this._maxZoom = defaultZoom * 10;
            this.applyFit(defaultZoom);
        }
    }
    applyFit(defaultZoom, box) {
        this.setZoom(defaultZoom);
        this.centerCamera(box);
        this.dispatchEvent({ type: "fit", args: null });
    }
    /**
     * Sets the camera position to absolute system origin.
     */
    centerCamera(box) {
        this._target.set(0, 0, 0);
        this.camera.position.set(0, 0, 5000);
        this.camera.updateProjectionMatrix();
        this._eye.subVectors(this.camera.position, this._target);
        this.camera.lookAt(this._target);
    }
    /**
     * Sets the camera position.
     */
    setCameraPosition(position) {
        this._target.copy(position).setZ(0);
        this.camera.position.copy(position);
        this._eye.subVectors(this.camera.position, this._target);
        this.camera.lookAt(this._target);
    }
    /**
    * Increases the camera zoom.
    */
    zoomIn() {
        this._zoomStart.y += 500 * 0.00025; //simulating mousewheel x5
    }
    /**
     * Decreases the camera zoom.
     */
    zoomOut() {
        this._zoomStart.y -= 500 * 0.00025; //simulating mousewheel x5
    }
    /**
     * Zoom by a factor.
     */
    zoomByFactor(factor) {
        const zoom = this.camera.zoom / factor;
        this.setZoom(zoom);
    }
    /**
     * Pan by a factor.
     */
    panByVector(vector) {
        this.camera.position.add(vector);
        this._target.add(vector);
    }
}
//# sourceMappingURL=CustomCameraControls.js.map